import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock3,
  LoaderCircle,
  RefreshCw,
  ServerCog,
  XCircle,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

const categories = [
  { id: "queued", label: "En cola", icon: Clock3 },
  { id: "active", label: "Activos", icon: Activity },
  { id: "failed", label: "Fallidos", icon: XCircle },
  { id: "finished", label: "Finalizados", icon: CheckCircle2 },
];

const rawStatusLabels = {
  running: "En ejecución",
  pending: "Pendiente",
  queued: "En cola",
  processing: "Procesando",
  preparing: "Preparando",
  uploading: "Guardando archivo",
  extracting_text: "Extrayendo texto",
  extracting_fields: "Extrayendo campos",
  generating_output: "Generando salida",
  awaiting_review: "Esperando revisión",
  extracting: "Extrayendo",
  indexing: "Indexando",
  inspecting: "Analizando",
  ocr: "Aplicando OCR",
  layout: "Ordenando texto",
  chunking: "Fragmentando",
  ready: "Listo",
  completed: "Completado",
  partial_failed: "Completado con errores",
  archived: "Archivado",
  failed: "Falló",
};

const taskTypeLabels = {
  maintenance: "Mantenimiento",
  queue: "Cola",
  scheduled: "Programado",
};

const configurationLabels = {
  aiActuacionBatchSize: "Actuaciones por lote de IA",
  batchCount: "Lotes por ejecución",
  concurrency: "Concurrencia",
  fullTextDelayMs: "Espera entre textos completos",
  includesCivitaCases: "Incluye expedientes de Civita",
  lockMs: "Duración del bloqueo",
  maxFileBytes: "Tamaño máximo de archivo",
  maxRegistration: "Matrícula máxima",
  minRegistration: "Matrícula mínima",
  pageSize: "Registros por página",
  pollIntervalMs: "Intervalo de consulta",
  productScope: "Alcance de productos",
  requestDelayMs: "Espera entre solicitudes",
  requestTimeoutMs: "Timeout de solicitud",
  retryAttempts: "Reintentos",
  runOnStartup: "Ejecuta al iniciar la API",
};

const productScopeLabels = {
  all: "Todos los productos",
  civita: "Sólo Civita",
  "non-civita": "Todos excepto Civita",
};

function automaticStateLabel(task) {
  if (task.automaticMode === "manual") return "Disponible · sólo manual";
  if (!task.automaticEnabled) return "Deshabilitado en esta API";
  if (task.automaticMode === "worker") return "Habilitado · worker activo";
  return task.schedule?.expression
    ? `Habilitado · cron ${task.schedule.expression}`
    : "Habilitado · programación activa";
}

function automaticStateClass(task) {
  if (task.automaticMode === "manual") return "is-manual";
  return task.automaticEnabled ? "is-enabled" : "is-disabled";
}

function durationLabel(milliseconds) {
  if (milliseconds < 1_000) return `${milliseconds} ms`;
  const seconds = milliseconds / 1_000;
  if (seconds < 60) return `${seconds.toLocaleString("es-AR")} s`;
  return `${(seconds / 60).toLocaleString("es-AR")} min`;
}

function bytesLabel(bytes) {
  const units = ["bytes", "KB", "MB", "GB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toLocaleString("es-AR", { maximumFractionDigits: 1 })} ${units[unit]}`;
}

function configurationValue(key, value) {
  if (typeof value === "boolean") return value ? "Sí" : "No";
  if (value == null || value === "") return "—";
  if (key === "productScope") return productScopeLabels[value] || value;
  if (key.endsWith("Ms") && Number.isFinite(value)) return durationLabel(value);
  if (key.endsWith("Bytes") && Number.isFinite(value)) return bytesLabel(value);
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return typeof value === "number" ? value.toLocaleString("es-AR") : String(value);
}

function taskConfiguration(task) {
  return [
    ...(task.schedule ? [
      ["cronExpression", "Expresión cron", task.schedule.expression],
      ["timezone", "Zona horaria", task.schedule.timezone],
      ["noOverlap", "Evita ejecuciones superpuestas", task.schedule.noOverlap ? "Sí" : "No"],
    ] : []),
    ...Object.entries(task.configuration || {}).map(([key, value]) => [
      key,
      configurationLabels[key] || key,
      configurationValue(key, value),
    ]),
  ];
}

const categoryLabels = Object.fromEntries(
  categories.map(({ id, label }) => [id, label]),
);

function dateTimeLabel(value) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("es-AR", {
    dateStyle: "short",
    timeStyle: "medium",
  }).format(new Date(value));
}

function relativeTime(value) {
  if (!value) return "";
  const seconds = Math.round((new Date(value).getTime() - Date.now()) / 1000);
  const formatter = new Intl.RelativeTimeFormat("es-AR", { numeric: "auto" });
  if (Math.abs(seconds) < 60) return formatter.format(seconds, "second");
  const minutes = Math.round(seconds / 60);
  if (Math.abs(minutes) < 60) return formatter.format(minutes, "minute");
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return formatter.format(hours, "hour");
  return formatter.format(Math.round(hours / 24), "day");
}

function activityTime(job) {
  return job.updatedAt || job.finishedAt || job.startedAt || job.createdAt;
}

function JobProgress({ job }) {
  if (!job.progress) {
    return <span className="jobs-progress-copy">{rawStatusLabels[job.rawStatus] || job.rawStatus}</span>;
  }
  const percent = Number.isFinite(job.progress.percent)
    ? Math.max(0, Math.min(100, job.progress.percent))
    : null;
  return (
    <div className="jobs-progress">
      <div>
        <span>{job.progress.message || rawStatusLabels[job.rawStatus] || job.rawStatus}</span>
        {percent !== null ? <strong>{percent}%</strong> : null}
      </div>
      {percent !== null ? <div className="jobs-progress-track"><span style={{ width: `${percent}%` }} /></div> : null}
    </div>
  );
}

function JobRow({ job }) {
  return (
    <article className="jobs-row">
      <div className="jobs-row-main">
        <strong>{job.title}</strong>
        <span>{job.description}</span>
        <small>{job.queueLabel}</small>
      </div>
      <div className="jobs-row-status">
        <span className={`jobs-status jobs-status-${job.category}`}>
          {categoryLabels[job.category] || job.category}
        </span>
        <small>{rawStatusLabels[job.rawStatus] || job.rawStatus}</small>
        {job.blocked ? <small className="jobs-warning">Bloqueado por otra etapa</small> : null}
        {job.claimable && job.category !== "finished" ? <small>Listo para reclamar</small> : null}
      </div>
      <JobProgress job={job} />
      <div className="jobs-row-time">
        <span title={dateTimeLabel(activityTime(job))}>{relativeTime(activityTime(job))}</span>
        <small>{dateTimeLabel(activityTime(job))}</small>
        {Number.isFinite(job.attempts) ? <small>{job.attempts} intento(s)</small> : null}
      </div>
      {job.error ? <div className="jobs-row-error"><AlertTriangle size={14} /> {job.error}</div> : null}
    </article>
  );
}

export function JobsAdminPage({ api }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [category, setCategory] = useState("all");
  const [queue, setQueue] = useState("all");
  const [historyHours, setHistoryHours] = useState(168);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (quiet) setRefreshing(true);
    else setLoading(true);
    try {
      setReport(await api.backgroundJobs({ historyHours, limit: 100 }));
      setError("");
    } catch (nextError) {
      setError(nextError.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [api, historyHours]);

  useEffect(() => { void load(); }, [load]);

  const hasLiveJobs = Boolean(
    (report?.summary?.queued || 0) + (report?.summary?.active || 0),
  );

  useEffect(() => {
    const timer = window.setInterval(
      () => void load({ quiet: true }),
      hasLiveJobs ? 5_000 : 30_000,
    );
    return () => window.clearInterval(timer);
  }, [hasLiveJobs, load]);

  const jobs = useMemo(() => (report?.queues || [])
    .flatMap((item) => item.items)
    .filter((job) => category === "all" || job.category === category)
    .filter((job) => queue === "all" || job.queue === queue)
    .sort((left, right) =>
      new Date(activityTime(right) || 0) - new Date(activityTime(left) || 0)),
  [category, queue, report]);

  const queueErrors = (report?.queues || []).filter(({ error: queueError }) => queueError);
  const selectedTask = queue === "all"
    ? null
    : (report?.queues || []).find((item) => item.id === queue) || null;
  const selectedConfiguration = selectedTask ? taskConfiguration(selectedTask) : [];

  return (
    <div className="support-access-page jobs-admin-page">
      <div className="support-access-heading jobs-heading">
        <div>
          <span className="support-access-eyebrow">OPERACIONES</span>
          <h1>Trabajos en segundo plano</h1>
          <p>Estado unificado de todos los jobs, cron y workers registrados en Tribunalea.</p>
          {report?.runtime ? (
            <div className="jobs-runtime">
              <span>API: {report.runtime.environment}</span>
              <span>{report.runtime.registeredTaskCount} trabajos registrados</span>
              <span>Cron global: {report.runtime.backgroundCronJobsEnabled ? "habilitado" : "deshabilitado"}</span>
              <span>Workers globales: {report.runtime.backgroundQueueWorkersEnabled ? "habilitados" : "deshabilitados"}</span>
            </div>
          ) : null}
        </div>
        <button className="jobs-refresh" onClick={() => load({ quiet: true })} disabled={refreshing || loading}>
          <RefreshCw size={15} className={refreshing ? "doctrine-spin" : ""} />
          Actualizar
        </button>
      </div>

      {error ? <div className="support-access-error">{error}</div> : null}
      {queueErrors.map((item) => (
        <div className="support-access-error" key={item.id}>
          {item.label}: {item.error}
        </div>
      ))}

      <div className="jobs-summary" aria-label="Resumen de trabajos">
        {categories.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            className={category === id ? "is-selected" : ""}
            onClick={() => setCategory((current) => current === id ? "all" : id)}
          >
            <Icon size={17} />
            <span>{label}</span>
            <strong>{report?.summary?.[id] || 0}</strong>
          </button>
        ))}
      </div>

      <section className="jobs-queues" aria-label="Trabajos monitoreados">
        <button className={queue === "all" ? "is-selected" : ""} onClick={() => setQueue("all")}>
          <ServerCog size={16} />
          <span>Todas las colas</span>
          <strong>{report?.queues?.reduce((sum, item) => sum + item.total, 0) || 0}</strong>
        </button>
        {(report?.queues || []).map((item) => (
          <button
            key={item.id}
            className={queue === item.id ? "is-selected" : ""}
            onClick={() => setQueue(item.id)}
            title={item.description}
          >
            <span>{item.label}</span>
            <strong>{item.total}</strong>
            <small>{item.counts.queued} cola · {item.counts.active} activos · {item.counts.failed} fallos</small>
            <small className={`jobs-queue-mode ${automaticStateClass(item)}`}>
              {taskTypeLabels[item.type] || item.type} · {automaticStateLabel(item)}
            </small>
          </button>
        ))}
      </section>

      {selectedTask ? (
        <section className="jobs-task-configuration" aria-label={`Configuración de ${selectedTask.label}`}>
          <div className="jobs-task-configuration-heading">
            <div>
              <span>CONFIGURACIÓN EFECTIVA DE ESTA API</span>
              <h2>{selectedTask.label}</h2>
              <p>{selectedTask.description}</p>
            </div>
            <span className={`jobs-task-availability ${automaticStateClass(selectedTask)}`}>
              {automaticStateLabel(selectedTask)}
            </span>
          </div>
          <dl>
            <div>
              <dt>Tipo</dt>
              <dd>{taskTypeLabels[selectedTask.type] || selectedTask.type}</dd>
            </div>
            <div>
              <dt>Registrado en la API</dt>
              <dd>{selectedTask.registered && selectedTask.runnable ? "Sí" : "No"}</dd>
            </div>
            {selectedConfiguration.map(([key, label, value]) => (
              <div key={key}>
                <dt>{label}</dt>
                <dd className={key === "cronExpression" ? "is-code" : ""}>{value}</dd>
              </div>
            ))}
          </dl>
        </section>
      ) : null}

      <div className="jobs-toolbar">
        <div>
          <strong>{jobs.length} trabajo(s)</strong>
          {report?.checkedAt ? <span>Actualizado {relativeTime(report.checkedAt)}</span> : null}
        </div>
        <label>
          Estado
          <select value={category} onChange={(event) => setCategory(event.target.value)}>
            <option value="all">Todos</option>
            {categories.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
          </select>
        </label>
        <label>
          Cola
          <select value={queue} onChange={(event) => setQueue(event.target.value)}>
            <option value="all">Todas</option>
            {(report?.queueDefinitions || []).map((item) => (
              <option key={item.id} value={item.id}>{item.label}</option>
            ))}
          </select>
        </label>
        <label>
          Historial
          <select value={historyHours} onChange={(event) => setHistoryHours(Number(event.target.value))}>
            <option value={24}>24 horas</option>
            <option value={168}>7 días</option>
            <option value={720}>30 días</option>
          </select>
        </label>
      </div>

      <section className="jobs-list">
        <div className="jobs-list-header">
          <span>Trabajo</span><span>Estado</span><span>Progreso</span><span>Actividad</span>
        </div>
        {loading ? (
          <div className="support-access-state"><LoaderCircle className="doctrine-spin" /> Cargando trabajos…</div>
        ) : jobs.length ? jobs.map((item) => <JobRow key={item.id} job={item} />) : (
          <div className="jobs-empty">
            <CheckCircle2 size={24} />
            {queue === "all"
              ? "No hay ejecuciones para estos filtros."
              : "Este trabajo todavía no tiene ejecuciones en el período seleccionado."}
          </div>
        )}
      </section>
    </div>
  );
}
